/**
 * CORE-AJ / CORE-AL — the two halves of the work lifecycle, named apart.
 *
 * ============================================================================
 * WHY THIS MODULE EXISTS
 * ============================================================================
 *
 * `stopTicketTimer` was never really a timer control. Repository history
 * deliberately made it a COMPLETION FUNNEL, and the Phase 4 caller inventory
 * showed the consequence: of its three live callers, only the Stop button
 * meant "stop work". `TicketCard.performCompletion()` and the EVE voice
 * handler both meant "complete this visit" — the eve-voice edge function even
 * says so, delegating completion to the client precisely so the funnel would
 * run.
 *
 * One function meaning two different things is how "stop the timer" came to
 * complete every job on the ticket, including jobs another technician still
 * had open.
 *
 * These two exports separate the intents AT THE API BOUNDARY, so a caller has
 * to say which one it means and a future reader cannot confuse them.
 *
 *   stopTicketWorkForReview   "I stopped productive work."
 *   completeTicketIfEligible  "This customer visit is finished."
 *
 * ============================================================================
 * WHAT STOPPING NO LONGER DOES
 * ============================================================================
 *
 * The timer authorities below (`stopTicketWorkForReview`, `endMyPausedWorkForReview`)
 * only end measured work: they complete no job, no visit, and release nothing.
 * A technician pauses for lunch, for parts, at end of day, or to hand work over.
 *
 * The technician's STOP button is different (`finishMyWork`, 20270102100000):
 * for the FINAL live BOH worker it is the deliberate declaration that the BOH
 * work is complete, and the server completes the ticket in the same act —
 * operational completion, never pickup, payment or closeout. With a colleague
 * still running or paused it ends only this technician's work.
 */
import { supabase } from '@/integrations/supabase/client';
import { triggerBoardCascade } from '@/utils/cascadeTrigger';
import { notifyDayboardInvalidated } from '@/utils/dayboardInvalidation';
import type { FinishWorkContext } from '@/lib/timer/interaction/simplifiedStop';

type Rpc = (fn: string, args: Record<string, unknown>) => Promise<{
  data: unknown;
  error: { message: string; code?: string; details?: string; hint?: string } | null;
  /** PostgREST surfaces these; the previous cast hid them from every caller. */
  status?: number;
  statusText?: string;
}>;

/** The RPCs below are newer than the generated types, so the cast is narrow. */
// RECEIVER-PRESERVING. `SupabaseClient.rpc` is a prototype method whose body is
// `return this.rest.rpc(...)`, so detaching it —
//
//     const rpc = supabase.rpc as unknown as Rpc;   // <- what this used to be
//
// — drops the receiver and throws `TypeError: Cannot read properties of
// undefined (reading 'rest')` before the request is ever sent. That is not
// theoretical: it is what production returned for the timer state read and the
// ticket delete, captured with http_status = null because nothing was sent.
//
// The wrapper below calls the method ON the client, so the receiver survives.
// Every call site is unchanged.
const rpc: Rpc = (fn, args) => (supabase.rpc as unknown as Rpc)(fn, args);

/**
 * The three states the ticket-level timer projection can actually hold.
 *
 * `reconcile_ticket_timer_projection` persists exactly these to
 * `tickets.timer_status`:
 *
 *     any session open            -> 'running'
 *     none open, a pause unresolved -> 'paused'
 *     neither                     -> 'stopped'
 *
 * `null` is the FOURTH possibility a caller must handle, and it is not a state
 * the ticket can be in — it means the projection could not be read. See
 * `readTicketTimerProjection`.
 */
export type TicketTimerProjection = 'running' | 'paused' | 'stopped';

/**
 * Read the ticket's persisted timer projection.
 *
 * WHY THIS IS A SEPARATE READ.
 * The canonical RPCs reconcile the projection into `tickets.timer_status`
 * inside their own transaction, but the value they hand back to the client is
 * `work_running` — a BOOLEAN. A boolean cannot express the middle state: on a
 * ticket where one technician stops while another is paused, `work_running` is
 * false and the ticket is `paused`, not `stopped`. The projection is already
 * persisted and frozen; nothing needs to change in the database for the client
 * to read the real value, so it reads it.
 *
 * Returns `null` when the value cannot be read or is not one of the three
 * known states. `null` means UNKNOWN. It must never be widened into
 * `'stopped'` by a caller — a failed read is not evidence that work ended.
 */
async function readTicketTimerProjection(
  ticketId: string,
): Promise<TicketTimerProjection | null> {
  try {
    const { data, error } = await supabase
      .from('tickets')
      .select('timer_status')
      .eq('id', ticketId)
      .maybeSingle();

    if (error) {
      console.warn('[readTicketTimerProjection] read failed', error);
      return null;
    }
    const status = (data as { timer_status?: unknown } | null)?.timer_status;
    return status === 'running' || status === 'paused' || status === 'stopped'
      ? status
      : null;
  } catch (e) {
    // A projection read is secondary. It may fail; it may not invent an answer,
    // and it may not turn a successful Stop into a thrown error.
    console.warn('[readTicketTimerProjection] threw', e);
    return null;
  }
}

// ============================================================================
// STOP WORK
// ============================================================================

export type StopWorkResult =
  | {
      outcome: 'STOPPED_AWAITING_REVIEW';
      /** The measurement session this Stop closed — one of possibly several. */
      sessionId: string;
      /**
       * The WORK EPISODE the session belonged to.
       *
       * A measurement session is not the unit a technician recognises. Pause
       * ends a session but the episode continues; Resume opens a new one under
       * the same episode. Everything below is EPISODE-scoped for that reason,
       * and this id is what binds them together.
       */
      workEpisodeId: string;
      /**
       * The persisted review row this Stop created. The proposal is frozen in
       * `ticket_timer_episode_reviews(_jobs)` at Stop, so a later code change
       * cannot silently re-derive a different proposal under the same review.
       */
      reviewId: string;
      /**
       * The algorithm version stamped on the frozen review
       * (`CORE_AJ_TIMER_ALLOC_V1`). Carried so a consumer can tell WHICH rules
       * produced the proposal it is displaying rather than assuming current
       * code produced it.
       */
      allocationAlgorithmVersion: string;
      /**
       * Measured minutes for the WHOLE EPISODE — every session under
       * `workEpisodeId`, not only the one this Stop closed.
       *
       * This previously read "for the session", which was wrong the moment
       * Pause/Resume existed: a technician who worked 90 minutes across a
       * pause would have seen only the post-resume session's minutes.
       */
      measuredMinutes: number;
      /**
       * The episode's minutes covered by selected job scope. Any shortfall
       * against `measuredMinutes` is productive time with no job selection and
       * stays ticket-level rather than being absorbed into the selected jobs.
       */
      attributableMinutes: number;
      /** The frozen review's per-job proposal, for the review step. */
      suggestion: Array<{
        job_id: string;
        suggested_minutes: number;
        direct_minutes: number;
        auto_proportional_minutes: number;
        fallback_minutes: number;
      }>;
      /**
       * The ticket's PERSISTED timer projection after this Stop —
       * `'running' | 'paused' | 'stopped'`, or `null` when it could not be read.
       *
       * This is the field a consumer should use. `null` is UNKNOWN and must be
       * carried as unknown; it is not a quiet synonym for `'stopped'`.
       */
      ticketTimerStatus: TicketTimerProjection | null;
      /**
       * Convenience only: `ticketTimerStatus === 'running'`.
       *
       * It was previously the ONLY signal, and it was a boolean, so a ticket
       * where another technician was PAUSED collapsed into "not running" and
       * then into "stopped". It survives because "is anyone still working?" is
       * a genuinely common question, but it is derived from the projection and
       * must not be used as a substitute for it.
       *
       * When the projection is unknown this is `false` — "we cannot show work
       * running" — which is the safe reading for a convenience flag precisely
       * because `ticketTimerStatus` still says `null` beside it.
       */
      ticketWorkStillRunning: boolean;
      /**
       * TRUE only when THIS Stop took the ticket from live BOH work to none:
       * no session left open and no unresolved pause, counted by the server
       * under the ticket transition lock.
       *
       * This — not `ticketTimerStatus`, which is re-read after commit for
       * display — is the fact a ticket-level decision (the optional FOH
       * handoff) must use. Two technicians stopping at once serialize on the
       * ticket row, so at most one of them can see `true`. Anything other than
       * a literal `true` from the server is read as `false`: a missing flag is
       * not evidence the ticket went idle.
       */
      ticketBecameIdle: boolean;
    }
  /**
   * THIS technician had nothing running. NOT a completion, and not necessarily
   * an idle ticket: another technician may be running or paused on it, which
   * `ticketTimerStatus` reports exactly, so a caller can say which of the three
   * happened instead of telling a technician the ticket is idle when it is not.
   */
  | {
      outcome: 'NO_ACTIVE_TIMER';
      ticketTimerStatus: TicketTimerProjection | null;
      ticketWorkStillRunning: boolean;
    }
  /**
   * Server refusals. Nothing was stopped. These used to fall through to
   * `STOPPED_AWAITING_REVIEW` with empty ids, which the UI then reported as a
   * successful Stop.
   */
  | { outcome: 'TICKET_NOT_AVAILABLE' }
  | { outcome: 'NOT_TECHNICIAN' }
  | { outcome: 'FAILED'; message: string };

/**
 * Read the server's `ticket_became_idle` strictly. Only a literal boolean
 * `true` counts; a missing or malformed value is "not idle", because the only
 * decision this feeds is whether the whole ticket may leave BOH.
 */
function becameIdle(r: Record<string, unknown>): boolean {
  return r.ticket_became_idle === true;
}

/**
 * Tell the board a work boundary happened.
 *
 * Stop and End Paused Work change `tickets.timer_status`, and the board
 * cascade treats a `running`/`paused` ticket as a locked anchor ending at its
 * `opti_finish` — so a stopped ticket changes downstream placement exactly as
 * a Pause does. Both are the same debounced, shop/date-keyed calls Pause and
 * the diagnostic Stop already make, so a burst of boundaries collapses into
 * one cascade. Best-effort: the Stop has already committed and a failed
 * refresh must not turn it into an error.
 */
async function announceWorkBoundary(ticketId: string): Promise<void> {
  try {
    const { data } = await supabase
      .from('tickets')
      .select('shop_id, scheduled_date')
      .eq('id', ticketId)
      .maybeSingle();
    const row = data as { shop_id?: string | null; scheduled_date?: string | null } | null;
    if (row?.shop_id && row?.scheduled_date) {
      void triggerBoardCascade(row.shop_id, row.scheduled_date);
    }
    await notifyDayboardInvalidated({
      reason: 'timer-update',
      // Empty -> local-only refetch; the helper skips the broadcast itself.
      shopId: row?.shop_id ?? '',
      ticketId,
    });
  } catch (e) {
    console.warn('[announceWorkBoundary] board refresh failed (non-fatal)', e);
  }
}

/**
 * Stop productive work on a session and prepare its allocation review.
 *
 * Routes to `stop_my_timer_for_review`, which stops the caller's OWN measured
 * session and closes the final selected-job segment at the SAME instant, so no
 * measured second is orphaned between the two records, then freezes the
 * episode's allocation proposal in the same transaction — there is no window
 * in which review is pending without a proposal to review.
 *
 * The review it prepares covers the WHOLE WORK EPISODE, not just the session
 * this call closed: every session the technician opened under the same episode
 * (across pauses and resumes) is reviewed together, because that is the unit of
 * work the technician actually performed.
 *
 * The ticket is left operationally open. Completion is a separate, deliberate
 * act.
 */
export async function stopTicketWorkForReview(
  ticketId: string,
): Promise<StopWorkResult> {
  // MULTI-TECH CORRECTION.
  // This previously read `tickets.current_session_id` — a SINGULAR pointer —
  // to decide which session to stop. On a ticket two technicians were working,
  // that pointer could be the OTHER technician's session, so pressing Stop
  // could stop someone else's timer and push their measured work into your
  // allocation review.
  //
  // The server now resolves the caller's OWN session from authenticated
  // identity. A client cannot name another technician's session at all.
  const { data, error } = await rpc('stop_my_timer_for_review', {
    p_ticket_id: ticketId,
  });

  if (error) {
    console.error('[stopTicketWorkForReview] failed', error);
    return { outcome: 'FAILED', message: error.message };
  }

  const r = (data ?? {}) as Record<string, unknown>;

  // EVERY outcome is classified explicitly. Refusals and anything unknown are
  // returned BEFORE any success shape is built — this function previously
  // treated every outcome other than NO_ACTIVE_TIMER as a successful Stop.
  if (r.outcome === 'TICKET_NOT_AVAILABLE' || r.outcome === 'NOT_TECHNICIAN') {
    return { outcome: r.outcome };
  }
  if (r.outcome !== 'STOPPED_AWAITING_REVIEW' && r.outcome !== 'NO_ACTIVE_TIMER') {
    return {
      outcome: 'FAILED',
      message: `Unexpected stop outcome: ${String(r.outcome ?? 'none')}`,
    };
  }

  // The projection is read AFTER the RPC has committed, for both outcomes, so
  // what the caller DISPLAYS is what the database actually holds rather than
  // what a boolean implies. On the no-op path this is the only way to tell
  // "someone else is running" from "someone else is paused" from "idle".
  // It is for display only: the ticket-level decision uses `ticketBecameIdle`.
  const projection = await readTicketTimerProjection(ticketId);

  if (r.outcome === 'NO_ACTIVE_TIMER') {
    // THIS technician has nothing running. Another technician may still be
    // working or paused on the ticket — that is not this caller's timer to
    // stop, and the caller must be able to say so rather than reporting an
    // idle ticket.
    return {
      outcome: 'NO_ACTIVE_TIMER',
      ticketTimerStatus: projection,
      ticketWorkStillRunning: projection === 'running',
    };
  }

  await announceWorkBoundary(ticketId);

  return {
    outcome: 'STOPPED_AWAITING_REVIEW',
    sessionId: String(r.session_id ?? ''),
    workEpisodeId: String(r.work_episode_id ?? ''),
    reviewId: String(r.review_id ?? ''),
    allocationAlgorithmVersion: String(r.allocation_algorithm_version ?? ''),
    measuredMinutes: Number(r.measured_minutes ?? 0),
    attributableMinutes: Number(r.attributable_minutes ?? 0),
    suggestion: (r.suggestion as never) ?? [],
    ticketTimerStatus: projection,
    // Derived from the authoritative projection, NOT from the RPC's boolean.
    // The two agree whenever the read succeeds; when it does not, this stays
    // false while `ticketTimerStatus` says `null`, so no caller can mistake an
    // unreadable projection for a stopped ticket.
    ticketWorkStillRunning: projection === 'running',
    ticketBecameIdle: becameIdle(r),
  };
}

// ============================================================================
// COMPLETE TICKET
// ============================================================================

/**
 * WHY THERE IS NO `completeTicketIfEligible` HERE ANY MORE.
 *
 * It called `complete_ticket_if_eligible`, which 8G.1.1 DROPPED. That function
 * could move a visit to Completed while asking for no location, no diagnostic
 * outcome and no comeback resolution, and it never released the bay — so it
 * was a second, weaker completion authority reachable by `authenticated`.
 *
 * 8G.0 recorded it as broken; what actually broke it was
 * `trg_attribute_ticket_to_bay`, and when 8G.1 dropped that trigger this
 * function silently started WORKING again. Measured at 8G.1's HEAD: it
 * completed a ticket. Two live authorities, one of which skipped every
 * obligation, is exactly the bypass CORE-AL and CORE-AO exist to prevent.
 *
 * Completion now lives in ONE place: `completeTicketWithLocation` in
 * `src/utils/ticketCompletionWithLocation.ts`, which requires the human's
 * location answer. No convenience wrapper is left here, because a convenient
 * old import is how the bypass comes back.
 *
 * `stopTicketWorkForReview` and End Paused Work are unaffected: stopping work
 * and ending a visit are different acts and always were.
 */

export function describeCompletionBlock(
  result: { outcome: string; reasons?: string[] },
): string {
  if (result.outcome === 'COMPLETED' || result.outcome === 'ALREADY_COMPLETED') return '';
  return result.reasons?.length
    ? result.reasons.join(' · ')
    : 'This ticket still has unfinished work.';
}

// ============================================================================
// END PAUSED WORK
// ============================================================================

/**
 * The technician's own paused work, ended and sent for allocation review.
 *
 * ── WHY THIS IS NOT "RESUME THEN STOP" ──────────────────────────────────────
 * A paused technician pressing End Work has finished. The tempting shortcut is
 * to call `resume_my_timer` and then `stop_my_timer_for_review`, because both
 * already exist — but that fabricates a productive interval that never
 * happened: a new session, opened and closed within the same instant, recorded
 * as measured work. Zero-duration or not, it is a claim that the technician
 * returned to the vehicle. `end_my_paused_work_for_review` resolves the pause
 * as ENDED_FOR_REVIEW and freezes the episode's proposal without inventing an
 * interval.
 *
 * The diagnostic Stop path is also wrong here and is not reachable from End
 * Work: there is no open measurement session to lock diagnostic minutes
 * against. Diagnostic reconciliation is 8C's.
 */
export type EndPausedWorkResult =
  | {
      outcome: 'ENDED_FOR_REVIEW';
      /** The episode whose proposal was frozen — all its sessions together. */
      workEpisodeId: string;
      /** The persisted review row this created. */
      reviewId: string;
      /** Measured minutes for the WHOLE episode, across every pause. */
      measuredMinutes: number;
      /** The episode's minutes covered by selected job scope. */
      attributableMinutes: number;
      /**
       * The ticket's persisted projection afterwards, or `null` if unknown.
       * The RPC reports it directly; the same narrow post-RPC read used by Stop
       * is the fallback, and `null` stays `null`.
       */
      ticketTimerStatus: TicketTimerProjection | null;
      /**
       * TRUE only when this act resolved the LAST live BOH work on the ticket:
       * no session open, no other unresolved pause, and there WAS a pause
       * before (so a no-op cannot claim the transition). Server-computed under
       * the ticket transition lock; a paused colleague keeps this `false`.
       */
      ticketBecameIdle: boolean;
    }
  /** THIS technician has no unresolved pause here. Not an error. */
  | { outcome: 'NO_PAUSED_WORK'; ticketTimerStatus: TicketTimerProjection | null }
  /** Server refusals — nothing was ended. */
  | { outcome: 'TICKET_NOT_AVAILABLE' }
  | { outcome: 'NOT_TECHNICIAN' }
  | { outcome: 'FAILED'; message: string };

export async function endMyPausedWorkForReview(
  ticketId: string,
): Promise<EndPausedWorkResult> {
  const { data, error } = await rpc('end_my_paused_work_for_review', {
    p_ticket_id: ticketId,
  });

  if (error) {
    console.error('[endMyPausedWorkForReview] failed', error);
    return { outcome: 'FAILED', message: error.message };
  }

  const r = (data ?? {}) as Record<string, unknown>;

  // The RPC returns the projection on its success path. The concurrent-resolve
  // no-op branch does not, so that case falls back to the same authorized read
  // Stop uses rather than assuming anything.
  const reported =
    r.ticket_timer_status === 'running' ||
    r.ticket_timer_status === 'paused' ||
    r.ticket_timer_status === 'stopped'
      ? (r.ticket_timer_status as TicketTimerProjection)
      : null;

  if (r.outcome === 'NO_PAUSED_WORK') {
    return {
      outcome: 'NO_PAUSED_WORK',
      ticketTimerStatus: reported ?? (await readTicketTimerProjection(ticketId)),
    };
  }

  if (r.outcome === 'TICKET_NOT_AVAILABLE' || r.outcome === 'NOT_TECHNICIAN') {
    return { outcome: r.outcome };
  }

  if (r.outcome !== 'ENDED_FOR_REVIEW') {
    return {
      outcome: 'FAILED',
      message: `Unexpected end-paused outcome: ${String(r.outcome ?? 'none')}`,
    };
  }

  // Ending paused work can move the ticket from `paused` to `stopped`, which
  // re-flows the board exactly like a Stop.
  await announceWorkBoundary(ticketId);

  return {
    outcome: 'ENDED_FOR_REVIEW',
    workEpisodeId: String(r.work_episode_id ?? ''),
    reviewId: String(r.review_id ?? ''),
    measuredMinutes: Number(r.measured_minutes ?? 0),
    attributableMinutes: Number(r.attributable_minutes ?? 0),
    ticketTimerStatus: reported ?? (await readTicketTimerProjection(ticketId)),
    ticketBecameIdle: becameIdle(r),
  };
}

// ============================================================================
// STAGE E — FINISH MY WORK (Stop / End Paused Work as one truthful act)
// ============================================================================

/**
 * What the Stop dialog needs to know before asking anything: is this the last
 * live BOH work, and is the vehicle in a bay this ticket holds? ADVISORY — the
 * server re-decides both under the ticket lock when the act is submitted.
 */
export async function readMyFinishWorkContext(ticketId: string): Promise<FinishWorkContext | null> {
  const { data, error } = await rpc('my_finish_work_context', { p_ticket_id: ticketId });
  if (error) throw new Error(error.message);
  const r = (data ?? {}) as Record<string, unknown>;
  if (r.outcome !== 'OK') return null;
  const loc = r.location as Record<string, string | null> | null;
  return {
    myState: r.my_state === 'ACTIVE' || r.my_state === 'PAUSED' ? r.my_state : 'NONE',
    othersLive: r.others_live === true,
    finalWorker: r.final_worker === true,
    holdsLocation: r.holds_location === true,
    location: loc ? {
      kind: String(loc.kind), bayId: loc.bay_id ?? null,
      parkingSpotId: loc.parking_spot_id ?? null, offSiteReason: loc.off_site_reason ?? null,
    } : null,
    needsDestination: r.needs_destination === true,
    unscopedJobs: Array.isArray(r.unscoped_jobs)
      ? (r.unscoped_jobs as Array<Record<string, unknown>>).map((j) => ({ jobId: String(j.job_id), name: String(j.name ?? 'Job') }))
      : [],
    // oct20 WS-STOP (D-2 / FD-02 / CORE-AJ :46) — advisory facts.
    rolloverSegmentContinues: r.rollover_segment_continues === true,
    completionWithheld: r.completion_withheld === true,
    openJobs: Array.isArray(r.open_jobs)
      ? (r.open_jobs as Array<Record<string, unknown>>).map((j) => ({
        jobId: String(j.job_id), name: String(j.name ?? 'Job'),
        kind: j.kind === 'diagnostic' || j.kind === 'inspection' ? j.kind : null,
        inMyScope: j.in_my_scope === true,
      }))
      : [],
    diagnosticOutcomeRequired: Array.isArray(r.diagnostic_outcome_required)
      ? (r.diagnostic_outcome_required as Array<Record<string, unknown>>).map((j) => ({ jobId: String(j.job_id), name: String(j.name ?? 'Job') }))
      : [],
    completionBlockers: Array.isArray(r.completion_blockers) ? (r.completion_blockers as unknown[]).map(String) : [],
    // oct20a S1: a Stop that ends my diagnostic session but not the diagnosis owes a session conclusion.
    diagnosticSessionConclusionRequired: jobList(r.diagnostic_session_conclusion_required),
  };
}

/** A server job list ({ job_id, name }[]) in the client's words. */
function jobList(v: unknown): Array<{ jobId: string; name: string }> {
  return Array.isArray(v)
    ? (v as Array<Record<string, unknown>>).map((j) => ({ jobId: String(j.job_id), name: String(j.name ?? 'Job') }))
    : [];
}

/** Why a final Stop could not complete the ticket — nothing changed. */
export type CompletionObligation =
  | 'DIAGNOSTIC_REQUIRED'
  | 'COMEBACK_REQUIRED'
  | 'NOT_ELIGIBLE'
  | 'TICKET_NOT_COMPLETABLE'
  | 'PAUSED_WORK'
  | 'COMPLETION_REFUSED';

const COMPLETION_OBLIGATIONS: ReadonlySet<string> = new Set([
  'DIAGNOSTIC_REQUIRED', 'COMEBACK_REQUIRED', 'NOT_ELIGIBLE', 'TICKET_NOT_COMPLETABLE', 'PAUSED_WORK', 'COMPLETION_REFUSED',
]);

export type FinishWorkResult =
  | {
      outcome: 'FINISHED';
      /** Which unchanged authority ended the work. */
      ended: 'STOPPED_AWAITING_REVIEW' | 'ENDED_FOR_REVIEW';
      workEpisodeId: string;
      reviewId: string;
      measuredMinutes: number;
      attributableMinutes: number;
      capturedJobCount: number;
      ticketTimerStatus: TicketTimerProjection | null;
      /** The server's ticket_became_idle: this was the last live BOH work. */
      ticketBecameIdle: boolean;
      vehicleMoved: boolean;
      /** The custody event of the automatic FOH handoff, or null (none happened). */
      fohHandoffEventId: string | null;
      /** The final Stop completed the ticket (operational completion only). */
      ticketCompleted: boolean;
      /** Core-100 direction 9: a Detail Specialist's last Stop on a visit with other work —
       *  their work ended, the visit was left for the front office to finish.
       *  oct20 FD-02: also the final Stop that left work unfinished or could not
       *  complete — work ended, ticket NOT completed, handed to the front office. */
      completionWithheld: boolean;
      /** oct20: why completion was withheld (the server's words). */
      withheldReasons?: string[];
      /** oct20 D-2: a rollover day with a live next day — only that day's work ended. */
      rolloverSegmentContinues?: boolean;
      /** OCT28 #14: "Entire repair complete" — the repair completed early and its unused later days were released. */
      entireRepair?: boolean;
      laterDaysReleased?: number;
      /** OCT28 #14-c: each inspection of the repair as the front office sees it. */
      inspections?: InspectionOutcome[];
    }
  /** Jobs with no frozen scope — or (OCT28 #14-c) open inspections — need the final technician's answer. Nothing changed. */
  | { outcome: 'JOB_DISPOSITION_REQUIRED'; jobs: DispositionJob[] }
  /** OCT28 #14 — Entire repair complete refused. Nothing changed: the timer runs, tomorrow's work is still booked. */
  | { outcome: 'ENTIRE_REPAIR_NOT_COMPLETED'; reasons: string[] }
  /** A later day of this repair has already been worked. Nothing changed. */
  | { outcome: 'SUCCESSOR_STARTED' }
  /** The repair is already completed (a repeat or a second device). Nothing changed. */
  | { outcome: 'ALREADY_COMPLETED' }
  /**
   * oct20 — CORE-AJ :46: a diagnostic in this technician's own work has no
   * outcome, and this Stop would end the diagnosis. Nothing changed.
   */
  | { outcome: 'DIAGNOSTIC_OUTCOME_REQUIRED'; jobs: Array<{ jobId: string; name: string }>; resumeRequired: boolean }
  /**
   * oct20a S1: this Stop ends the technician's diagnostic SESSION but not the diagnosis — what the
   * session found (a session conclusion) is owed for these jobs. Nothing changed.
   */
  | { outcome: 'SESSION_CONCLUSION_REQUIRED'; jobs: Array<{ jobId: string; name: string }> }
  | { outcome: 'INVALID_SESSION_CONCLUSION'; reason: string }
  | { outcome: 'INVALID_JOB_DISPOSITION'; reason?: string; jobs?: Array<{ jobId: string; name: string }> }
  /** A completed visit is on site: off site is not where the final Stop can leave it. Nothing changed. */
  | { outcome: 'COMPLETION_NEEDS_ON_SITE' }
  /** The ticket cannot be completed yet (the reason is the server's). Nothing changed. */
  | { outcome: 'COMPLETION_BLOCKED'; obligation: CompletionObligation; reasons: string[] }
  /** The final worker is leaving a bay: where to? Nothing changed. */
  | { outcome: 'DESTINATION_REQUIRED' }
  /** That destination cannot take the vehicle (occupied, inactive…). Nothing changed. */
  | { outcome: 'DESTINATION_NOT_AVAILABLE'; detail: string }
  | { outcome: 'INVALID_DESTINATION' }
  /** This technician has no running or paused work here (or a duplicate lost the race). */
  | { outcome: 'NO_WORK_TO_FINISH'; ticketTimerStatus: TicketTimerProjection | null }
  | { outcome: 'NO_ACTIVE_TIMER' }
  | { outcome: 'NO_PAUSED_WORK' }
  | { outcome: 'CAPTURE_SCOPE_STALE'; invalidJobIds: string[] }
  | { outcome: 'CAPTURE_ALREADY_RECORDED'; invalidJobIds: string[] }
  | { outcome: 'INVALID_CAPTURE'; reason: string }
  | { outcome: 'TICKET_NOT_AVAILABLE' }
  | { outcome: 'NOT_TECHNICIAN' }
  | { outcome: 'FAILED'; message: string };

/** OCT28 #14-c: an inspection's state after the final Stop (job_status completed / open / skipped). */
export interface InspectionOutcome { jobId: string; name: string; outcome: 'COMPLETED' | 'INCOMPLETE' | 'NOT_PERFORMED' }
/** A job the final Stop needs an answer for. `worked` (inspections): timer evidence covers it — never "Not performed". */
export interface DispositionJob { jobId: string; name: string; kind?: 'inspection' | null; worked?: boolean }

const FINISH_REFUSED_MARKER = 'FINISH_WORK_REFUSED:';

/** A refusal (from a server answer or from the rolled-back marker), classified strictly. */
function classifyFinishRefusal(r: Record<string, unknown>): FinishWorkResult {
  switch (r.outcome) {
    case 'DESTINATION_REQUIRED': return { outcome: 'DESTINATION_REQUIRED' };
    case 'DESTINATION_NOT_AVAILABLE': return { outcome: 'DESTINATION_NOT_AVAILABLE', detail: String(r.detail ?? 'That location cannot take the vehicle.') };
    case 'INVALID_DESTINATION': return { outcome: 'INVALID_DESTINATION' };
    case 'NO_WORK_TO_FINISH': {
      const s = r.ticket_timer_status;
      return { outcome: 'NO_WORK_TO_FINISH',
        ticketTimerStatus: s === 'running' || s === 'paused' || s === 'stopped' ? s : null };
    }
    case 'NO_ACTIVE_TIMER':
    case 'NO_PAUSED_WORK':
    case 'TICKET_NOT_AVAILABLE':
    case 'NOT_TECHNICIAN':
      return { outcome: r.outcome };
    case 'CAPTURE_SCOPE_STALE':
    case 'CAPTURE_ALREADY_RECORDED':
      return { outcome: r.outcome, invalidJobIds: (r.invalid_job_ids as string[]) ?? [] };
    case 'INVALID_CAPTURE': return { outcome: 'INVALID_CAPTURE', reason: String(r.reason ?? 'invalid') };
    case 'JOB_DISPOSITION_REQUIRED':
      return { outcome: 'JOB_DISPOSITION_REQUIRED',
        jobs: Array.isArray(r.jobs)
          ? (r.jobs as Array<Record<string, unknown>>).map((j) => ({ jobId: String(j.job_id), name: String(j.name ?? 'Job'),
              ...(j.kind === 'inspection' ? { kind: 'inspection' as const, worked: j.worked === true } : {}) }))
          : [] };
    case 'INVALID_JOB_DISPOSITION':
      return { outcome: 'INVALID_JOB_DISPOSITION', ...(typeof r.reason === 'string' ? { reason: r.reason } : {}),
        ...(Array.isArray(r.jobs) ? { jobs: jobList(r.jobs) } : {}) };
    case 'SUCCESSOR_STARTED':
    case 'ALREADY_COMPLETED':
      return { outcome: r.outcome };
    case 'NOT_AUTHENTICATED':
      return { outcome: 'FAILED', message: 'You are signed out. Sign in again, then stop.' };
    case 'SUCCESSOR_NOT_RELEASABLE':
      return { outcome: 'ENTIRE_REPAIR_NOT_COMPLETED', reasons: ['a later day of this repair could not be released'] };
    case 'SESSION_CONCLUSION_REQUIRED': return { outcome: 'SESSION_CONCLUSION_REQUIRED', jobs: jobList(r.jobs) };
    case 'INVALID_SESSION_CONCLUSION': return { outcome: 'INVALID_SESSION_CONCLUSION', reason: String(r.reason ?? 'invalid') };
    case 'COMPLETION_NEEDS_ON_SITE': return { outcome: 'COMPLETION_NEEDS_ON_SITE' };
    case 'DIAGNOSTIC_REQUIRED':
      // oct20: the CORE-AJ :46 session gate names the jobs; otherwise it is a completion obligation.
      if (r.gate === 'DIAGNOSTIC_SESSION') {
        return { outcome: 'DIAGNOSTIC_OUTCOME_REQUIRED', resumeRequired: r.resume_required === true,
          jobs: Array.isArray(r.jobs)
            ? (r.jobs as Array<Record<string, unknown>>).map((j) => ({ jobId: String(j.job_id), name: String(j.name ?? 'Job') }))
            : [] };
      }
      return { outcome: 'COMPLETION_BLOCKED', obligation: 'DIAGNOSTIC_REQUIRED',
        reasons: Array.isArray(r.reasons) ? (r.reasons as unknown[]).map(String) : [] };
    default:
      if (typeof r.outcome === 'string' && COMPLETION_OBLIGATIONS.has(r.outcome)) {
        return { outcome: 'COMPLETION_BLOCKED', obligation: r.outcome as CompletionObligation,
          reasons: Array.isArray(r.reasons) ? (r.reasons as unknown[]).map(String) : [] };
      }
      return { outcome: 'FAILED', message: `Unexpected finish outcome: ${String(r.outcome ?? 'none')}` };
  }
}

/**
 * The technician Stop — ONE server act (`finish_my_work`).
 *
 * Running → Stop for review (with optional diagnostic findings); paused → End
 * Paused Work. If — and only if — the server finds this was the last live BOH
 * work, the same transaction completes the ticket (jobs through their frozen
 * evidence or the technician's `dispositions`), leaves the vehicle on site at
 * `destination` and hands the ticket to the FOH queue. A refusal changes
 * nothing: timer, jobs, vehicle, completion and custody stay as they were.
 * Unknown = FAILED.
 */
export async function finishMyWork(
  ticketId: string,
  destination: Record<string, string> | null,
  captures: Array<{ jobId: string; outcome: string; competencyOutcome: string; confidence: number; complaintConfirmed: boolean }>,
  dispositions: Array<{ job_id: string; disposition: string }> = [],
  /** oct20a S1: what this session found, per diagnostic job (sessionConclusionPayload). Sent only when given. */
  sessionConclusions: ReadonlyArray<Record<string, unknown>> = [],
  /**
   * OCT28 #14: "Entire repair complete" — the same Stop through `finish_entire_repair`, which also releases the
   * repair's unused later days, all or nothing. Only the server decides; the browser never touches those days.
   */
  opts: { entireRepair?: boolean } = {},
): Promise<FinishWorkResult> {
  const { data, error } = await rpc(opts.entireRepair ? 'finish_entire_repair' : 'finish_my_work', {
    p_ticket_id: ticketId,
    p_destination: destination,
    // Only the technician's own assertions — the server derives everything else.
    p_captures: captures.map((c) => ({
      job_id: c.jobId, outcome: c.outcome, competency_outcome: c.competencyOutcome,
      confidence: c.confidence, complaint_confirmed: c.complaintConfirmed,
    })),
    p_job_dispositions: dispositions,
    ...(sessionConclusions.length > 0 ? { p_session_conclusions: sessionConclusions } : {}),
  });

  if (error) {
    const at = error.message.indexOf(FINISH_REFUSED_MARKER);
    if (at >= 0) {
      // The server refused after moving the vehicle and rolled BOTH back.
      try {
        return classifyFinishRefusal(JSON.parse(error.message.slice(at + FINISH_REFUSED_MARKER.length)));
      } catch {
        return { outcome: 'FAILED', message: error.message };
      }
    }
    console.error('[finishMyWork] failed', error);
    return { outcome: 'FAILED', message: error.message };
  }

  const r = (data ?? {}) as Record<string, unknown>;
  if (opts.entireRepair && r.entire_repair !== true) {
    // A refused Entire repair answers with the inner Stop's own body, which can LOOK like a finished Stop (e.g. work
    // left unfinished) — but the whole act was rolled back. Never read it as a success.
    if (r.outcome === 'STOPPED_AWAITING_REVIEW' || r.outcome === 'ENDED_FOR_REVIEW') {
      return { outcome: 'ENTIRE_REPAIR_NOT_COMPLETED',
        reasons: Array.isArray(r.withheld_reasons) ? (r.withheld_reasons as unknown[]).map(String) : [] };
    }
    if (r.outcome === 'ALREADY_COMPLETED') return { outcome: 'ALREADY_COMPLETED' };
    return classifyFinishRefusal(r);
  }
  if (r.outcome !== 'STOPPED_AWAITING_REVIEW' && r.outcome !== 'ENDED_FOR_REVIEW') {
    return classifyFinishRefusal(r);
  }

  await announceWorkBoundary(ticketId);
  const handoff = r.foh_handoff as { custody_event_id?: string } | null;
  const reported = r.ticket_timer_status === 'running' || r.ticket_timer_status === 'paused'
    || r.ticket_timer_status === 'stopped' ? (r.ticket_timer_status as TicketTimerProjection) : null;
  return {
    outcome: 'FINISHED',
    ended: r.outcome,
    workEpisodeId: String(r.work_episode_id ?? ''),
    reviewId: String(r.review_id ?? ''),
    measuredMinutes: Number(r.measured_minutes ?? 0),
    attributableMinutes: Number(r.attributable_minutes ?? 0),
    capturedJobCount: Number(r.captured_job_count ?? 0),
    ticketTimerStatus: reported ?? (await readTicketTimerProjection(ticketId)),
    ticketBecameIdle: becameIdle(r),
    vehicleMoved: r.vehicle_moved === true,
    fohHandoffEventId: handoff?.custody_event_id ? String(handoff.custody_event_id) : null,
    ticketCompleted: r.ticket_completed === true,
    completionWithheld: r.completion_withheld === true,
    withheldReasons: Array.isArray(r.withheld_reasons) ? (r.withheld_reasons as unknown[]).map(String) : [],
    rolloverSegmentContinues: r.rollover_segment_continues === true,
    entireRepair: r.entire_repair === true,
    laterDaysReleased: Array.isArray(r.later_days_released) ? r.later_days_released.length : 0,
    inspections: Array.isArray(r.inspections)
      ? (r.inspections as Array<Record<string, unknown>>).map((x) => ({ jobId: String(x.job_id), name: String(x.name ?? 'Inspection'),
          outcome: x.outcome === 'COMPLETED' || x.outcome === 'NOT_PERFORMED' ? x.outcome : 'INCOMPLETE' as const }))
      : [],
  };
}

// ============================================================================
// MY TIMER CONTROL STATE
// ============================================================================

/**
 * The CALLER's own work state on a ticket.
 *
 * ── WHY THE TICKET CANNOT ANSWER THIS ───────────────────────────────────────
 * The controls used to be chosen from `tickets.timer_status`: `running` gave
 * Pause + Stop, `paused` gave Resume + Stop, `stopped` gave Start. That is a
 * statement about the TICKET. On a ticket two technicians share it is not a
 * statement about the person holding the phone — a technician whose own work
 * was paused saw Pause + Stop because a COLLEAGUE was running, and a technician
 * who had never touched the ticket saw the same.
 *
 * ── THIS IS DISPLAY AUTHORITY ONLY ──────────────────────────────────────────
 * Every action revalidates server-side. Between this read and the button press
 * the technician may have started a timer on another device, so a stale `IDLE`
 * here must never be what makes a Start correct — `start_my_timer_with_scope`
 * returning `ALREADY_HAS_ACTIVE_TIMER` is the authority, and the UI simply
 * reports it.
 */
export type MyTimerState =
  /** Caller has an active measured session on THIS ticket. */
  | 'ACTIVE'
  /** Caller has an unresolved canonical pause on THIS ticket. */
  | 'PAUSED'
  /** Caller has stopped evidence on THIS ticket awaiting allocation review. */
  | 'AWAITING_REVIEW'
  /** None of the above on this ticket. */
  | 'IDLE'
  /** Caller is not a technician in this ticket's shop. */
  | 'NOT_TECHNICIAN'
  /** Canonical facts contradict each other. No control may be offered. */
  | 'INTEGRITY_ERROR'
  /** The state could not be read. Also not a licence to guess. */
  | 'UNKNOWN';

export interface MyTimerControlState {
  myState: MyTimerState;
  shopId: string | null;
  technicianId: string | null;
  workEpisodeId: string | null;
  sessionId: string | null;
  /** The TICKET's projection — still useful for ticket-level display. */
  ticketTimerStatus: TicketTimerProjection | null;
  /** The caller's unreviewed episodes across ALL tickets. Blocks a new Start. */
  pendingReviewCount: number;
}

const UNKNOWN_CONTROL_STATE: MyTimerControlState = {
  myState: 'UNKNOWN',
  shopId: null,
  technicianId: null,
  workEpisodeId: null,
  sessionId: null,
  ticketTimerStatus: null,
  pendingReviewCount: 0,
};


// ═══════════════════════════════════════════════════════════════════════════
// TEMPORARY DIAGNOSTIC — remove with `tmp_timer_control_diagnostics`
// ═══════════════════════════════════════════════════════════════════════════
//
// "Checking timer…" is what the UI shows for UNKNOWN, and UNKNOWN is where FOUR
// different situations collapse: a token the client does not recognise, a
// PostgREST error, a REJECTED promise, and the initial pre-read value. The
// symptom cannot tell them apart, no server-side log of the request survives,
// and repository evidence cannot establish which identity the browser actually
// presents.
//
// So this records the distinction once, from the only place that sees all four.
// It is observation only: `readMyTimerControlState` returns exactly what it
// returned before, in every case, and this never throws into its caller.
//
// Metadata only — no tokens, no keys, no customer, vehicle or job data. The
// recorded `user_id` is forced to `auth.uid()` by a database trigger, so it is
// the identity the request actually authenticated as rather than one the
// browser claimed.
//
// KNOWN LIMITATION, stated rather than engineered around: this sink is Supabase.
// The app has no `/api` route, no Vercel function, and its CSP `connect-src`
// permits only self and Supabase — so if the session itself is invalid, the
// diagnostic INSERT fails for the same reason the RPC did and NO ROW APPEARS.
// An absent row after a confirmed refresh is therefore itself a finding.

/** The one ticket we want recorded even on success, to prove runtime identity. */
const DIAG_ALWAYS_RECORD_TICKET_ID = 'cc5afcb2-3524-4404-85a6-db0d713b3390';

type DiagResultClass =
  | 'KNOWN_STATE'
  | 'UNKNOWN_STATE_TOKEN'
  | 'RPC_ERROR'
  | 'RPC_THROW';

/** Bounded, so a diagnostic row can never become a place to deposit a payload. */
const diagClip = (v: unknown, max: number): string | null => {
  if (v === null || v === undefined) return null;
  const t = typeof v === 'string' ? v : String(v);
  return t.length > max ? t.slice(0, max) : t;
};

/** The deployed bundle, for the "is a stale client cached?" question. */
function diagBuildId(): string | null {
  try {
    const src = (document.querySelector('script[src*="/assets/index-"]') as HTMLScriptElement | null)?.src;
    return src ? diagClip(src.split('/').pop(), 128) : null;
  } catch {
    return null;
  }
}

/**
 * Fire and forget. Never awaited by the caller, never throws, never changes the
 * value `readMyTimerControlState` returns.
 */
function recordTimerControlDiagnostic(row: {
  ticketId: string;
  resultClass: DiagResultClass;
  myState?: unknown;
  httpStatus?: number;
  statusText?: unknown;
  pgErrorCode?: unknown;
  errorClass?: unknown;
  errorMessage?: unknown;
}): void {
  try {
    if (row.resultClass === 'KNOWN_STATE' && row.ticketId !== DIAG_ALWAYS_RECORD_TICKET_ID) {
      // Only failures and unrecognised tokens are interesting in general; the
      // one ticket under investigation is recorded either way so we can prove
      // its runtime identity and state even when nothing looks wrong.
      return;
    }

    void (async () => {
      let claimed: string | null = null;
      let hasSession = false;
      try {
        const { data } = await supabase.auth.getSession();
        hasSession = !!data.session;
        claimed = data.session?.user?.id ?? null;
      } catch {
        /* the client's own view is a nicety; its absence is not an error */
      }

      await supabase.from('tmp_timer_control_diagnostics' as never).insert({
        // `user_id` is deliberately NOT sent. The database trigger sets it from
        // auth.uid(); anything sent here would be overwritten anyway.
        ticket_id: diagClip(row.ticketId, 128),
        result_class: row.resultClass,
        my_state: diagClip(row.myState, 64),
        http_status: typeof row.httpStatus === 'number' ? row.httpStatus : null,
        status_text: diagClip(row.statusText, 128),
        pg_error_code: diagClip(row.pgErrorCode, 32),
        error_class: diagClip(row.errorClass, 64),
        error_message: diagClip(row.errorMessage, 300),
        build_id: diagBuildId(),
        client_claimed_user_id: claimed,
        client_has_session: hasSession,
      } as never);
    })();
  } catch {
    /* diagnostics must never affect the thing they are diagnosing */
  }
}

export async function readMyTimerControlState(
  ticketId: string,
): Promise<MyTimerControlState> {
  // The whole response, not a destructure: `status` and `statusText` are the
  // difference between "PostgREST refused this" and "the token was unfamiliar".
  let res: Awaited<ReturnType<Rpc>>;
  try {
    res = await rpc('my_timer_control_state', { p_ticket_id: ticketId });
  } catch (thrown) {
    // A REJECTED promise, which the previous code could not see at all: it
    // propagated out of here, `setMyState` was never called, and the component
    // sat on its initial UNKNOWN forever — indistinguishable from loading.
    console.warn('[readMyTimerControlState] threw', thrown);
    recordTimerControlDiagnostic({
      ticketId,
      resultClass: 'RPC_THROW',
      errorClass: (thrown as { name?: string } | null)?.name ?? typeof thrown,
      errorMessage: (thrown as { message?: string } | null)?.message ?? String(thrown),
    });
    return UNKNOWN_CONTROL_STATE;
  }

  const { data, error } = res;

  if (error) {
    // UNKNOWN, not IDLE. Defaulting to IDLE would offer Start to a technician
    // who may already be running, and the failure would look like a normal
    // empty state instead of a failure.
    console.warn('[readMyTimerControlState] failed', error);
    recordTimerControlDiagnostic({
      ticketId,
      resultClass: 'RPC_ERROR',
      httpStatus: res.status,
      statusText: res.statusText,
      pgErrorCode: (error as { code?: string }).code,
      errorClass: 'PostgrestError',
      errorMessage: error.message,
    });
    return UNKNOWN_CONTROL_STATE;
  }

  const r = (data ?? {}) as Record<string, unknown>;
  const state = r.my_state;
  const known: MyTimerState[] = [
    'ACTIVE', 'PAUSED', 'AWAITING_REVIEW', 'IDLE', 'NOT_TECHNICIAN', 'INTEGRITY_ERROR',
  ];

  recordTimerControlDiagnostic({
    ticketId,
    resultClass: known.includes(state as MyTimerState) ? 'KNOWN_STATE' : 'UNKNOWN_STATE_TOKEN',
    myState: state,
    httpStatus: res.status,
    statusText: res.statusText,
  });

  return {
    myState: known.includes(state as MyTimerState) ? (state as MyTimerState) : 'UNKNOWN',
    shopId: (r.shop_id as string) ?? null,
    technicianId: (r.technician_id as string) ?? null,
    workEpisodeId: (r.work_episode_id as string) ?? null,
    sessionId: (r.session_id as string) ?? null,
    ticketTimerStatus:
      r.ticket_timer_status === 'running' ||
      r.ticket_timer_status === 'paused' ||
      r.ticket_timer_status === 'stopped'
        ? (r.ticket_timer_status as TicketTimerProjection)
        : null,
    pendingReviewCount: Number(r.pending_review_count ?? 0),
  };
}
